import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Lock, Mail, Globe, Trash2, Loader2, CheckCircle2, Camera } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import GoogleAvatar from "@/components/GoogleAvatar";
import PasswordField from "@/components/PasswordField";

const COPY = {
  en: {
    title: "Settings",
    tabs: { profile: "Profile", security: "Security", prefs: "Preferences", danger: "Account" },
    name: "Name", picture: "Avatar URL",
    upload_photo: "Upload photo", picture_hint: "JPG, PNG, WEBP or GIF, max 3 MB",
    current_pass: "Current password", new_pass: "New password",
    new_email: "New email", password: "Password (confirm)",
    change_pass: "Change password", change_email: "Change email", save: "Save",
    lang: "Preferred language",
    delete_title: "Delete account",
    delete_desc: "This will permanently erase your account and all associated data. This action cannot be undone.",
    delete_confirm: "Type DELETE to confirm",
    delete_btn: "Delete my account",
    saved: "Saved",
    err_no_change: "Nothing to save",
  },
  ru: {
    title: "Настройки",
    tabs: { profile: "Профиль", security: "Безопасность", prefs: "Настройки", danger: "Аккаунт" },
    name: "Имя", picture: "URL аватара",
    upload_photo: "Загрузить фото", picture_hint: "JPG, PNG, WEBP или GIF, до 3 МБ",
    current_pass: "Текущий пароль", new_pass: "Новый пароль",
    new_email: "Новый email", password: "Пароль (подтверждение)",
    change_pass: "Сменить пароль", change_email: "Сменить email", save: "Сохранить",
    lang: "Предпочитаемый язык",
    delete_title: "Удалить аккаунт",
    delete_desc: "Аккаунт и все связанные данные будут удалены безвозвратно.",
    delete_confirm: "Введите DELETE для подтверждения",
    delete_btn: "Удалить мой аккаунт",
    saved: "Сохранено",
    err_no_change: "Нет изменений",
  },
  es: {
    title: "Ajustes",
    tabs: { profile: "Perfil", security: "Seguridad", prefs: "Preferencias", danger: "Cuenta" },
    name: "Nombre", picture: "URL del avatar",
    upload_photo: "Subir foto", picture_hint: "JPG, PNG, WEBP o GIF, máx. 3 MB",
    current_pass: "Contraseña actual", new_pass: "Nueva contraseña",
    new_email: "Nuevo email", password: "Contraseña (confirmar)",
    change_pass: "Cambiar contraseña", change_email: "Cambiar email", save: "Guardar",
    lang: "Idioma preferido",
    delete_title: "Eliminar cuenta",
    delete_desc: "Esto eliminará permanentemente tu cuenta y todos los datos asociados. No se puede deshacer.",
    delete_confirm: "Escribe DELETE para confirmar",
    delete_btn: "Eliminar mi cuenta",
    saved: "Guardado",
    err_no_change: "Sin cambios",
  },
};

export default function SettingsModal({ open, onClose }) {
  const { lang, setLang } = useLang();
  const c = COPY[lang] || COPY.en;
  const { user, setUser, logout, checkAuth } = useAuth();
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!user || user === false) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="settings-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-[#0a0a0a]/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white border border-[#0a0a0a] shadow-[8px_8px_0_0_rgba(10,10,10,1)]"
          >
            <div className="px-6 py-4 border-b border-[#0a0a0a] flex items-center justify-between">
              <h3 className="font-mono font-bold tracking-tighter text-xl text-[#0a0a0a]">{c.title}</h3>
              <button onClick={onClose} data-testid="settings-close" className="p-1 hover:text-[#ff006e]">
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col md:flex-row min-h-[440px]">
              <aside className="md:w-48 border-b md:border-b-0 md:border-r border-[#0a0a0a] flex md:flex-col overflow-x-auto">
                {Object.entries(c.tabs).map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`settings-tab-${k}`}
                    onClick={() => setTab(k)}
                    className={`flex items-center gap-2 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-left border-b border-[#e5e5e5] transition-colors whitespace-nowrap ${
                      tab === k ? "bg-[#0a0a0a] text-white" : "text-[#0a0a0a] hover:bg-[#f5f5f5]"
                    }`}
                  >
                    {k === "profile" && <User size={12} />}
                    {k === "security" && <Lock size={12} />}
                    {k === "prefs" && <Globe size={12} />}
                    {k === "danger" && <Trash2 size={12} />}
                    {label}
                  </button>
                ))}
              </aside>

              <div className="flex-1 p-6 space-y-5">
                {tab === "profile" && <ProfileTab c={c} user={user} setUser={setUser} />}
                {tab === "security" && <SecurityTab c={c} user={user} onEmailChanged={checkAuth} />}
                {tab === "prefs" && <PrefsTab c={c} user={user} setUser={setUser} lang={lang} setLang={setLang} />}
                {tab === "danger" && <DangerTab c={c} user={user} logout={logout} />}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a] mb-2">{label}</div>
      {children}
    </label>
  );
}
function Input(props) {
  return <input {...props} className={`w-full px-3 py-2.5 border border-[#0a0a0a] bg-white text-[#0a0a0a] font-sans text-sm focus:outline-none focus:border-[#ff006e] ${props.className || ""}`} />;
}
function BtnPrimary({ children, ...rest }) {
  return <button {...rest} className={`inline-flex items-center gap-2 px-5 py-2.5 bg-[#0a0a0a] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors disabled:opacity-50 ${rest.className || ""}`}>{children}</button>;
}
function BtnDanger({ children, ...rest }) {
  return <button {...rest} className={`inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff006e] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#0a0a0a] transition-colors disabled:opacity-50 ${rest.className || ""}`}>{children}</button>;
}
function Feedback({ ok, err, c }) {
  if (ok) return <div className="font-mono text-xs text-[#00d4ff] inline-flex items-center gap-2"><CheckCircle2 size={12}/> {c.saved}</div>;
  if (err) return <div className="font-mono text-xs text-[#ff006e]">{err}</div>;
  return null;
}

function ProfileTab({ c, user, setUser }) {
  const [name, setName] = useState(user.name || "");
  const [picture, setPicture] = useState(user.picture || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = React.useRef(null);

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      const { data } = await api.patch("/auth/me", { name, picture });
      setUser({ ...user, ...data, password_hash: undefined });
      setOk(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr(""); setOk(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(data.picture);
      setUser({ ...user, picture: data.picture });
      setOk(true);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5">
      {/* Avatar with camera overlay (Gmail style) — no URL field needed */}
      <div className="flex items-center gap-5">
        <div className="relative">
          <GoogleAvatar user={{ ...user, picture }} size={76} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="settings-avatar-camera"
            aria-label={c.upload_photo}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border-2 border-[#0a0a0a] flex items-center justify-center text-[#0a0a0a] hover:bg-[#ff006e] hover:text-white hover:border-[#ff006e] transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={upload}
            className="hidden"
            data-testid="settings-avatar-file"
          />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a] max-w-[180px] leading-relaxed">
          {c.picture_hint}
        </p>
      </div>
      <Row label={c.name}>
        <Input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} />
      </Row>
      <div className="flex items-center justify-between pt-2 border-t border-dashed border-[#e5e5e5]">
        <Feedback ok={ok} err={err} c={c} />
        <BtnPrimary onClick={save} disabled={busy} data-testid="settings-save-profile">
          {busy ? <Loader2 size={12} className="animate-spin" /> : c.save}
        </BtnPrimary>
      </div>
    </div>
  );
}

function SecurityTab({ c, user, onEmailChanged }) {
  const [pass, setPass] = useState({ current: "", next: "" });
  const [busyP, setBusyP] = useState(false);
  const [okP, setOkP] = useState(false);
  const [errP, setErrP] = useState("");

  const [email, setEmail] = useState({ new_email: "", current_password: "" });
  const [busyE, setBusyE] = useState(false);
  const [okE, setOkE] = useState(false);
  const [errE, setErrE] = useState("");

  const changePass = async () => {
    setBusyP(true); setErrP(""); setOkP(false);
    try {
      await api.post("/auth/change-password", { current_password: pass.current, new_password: pass.next });
      setOkP(true); setPass({ current: "", next: "" });
    } catch (e) {
      setErrP(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusyP(false); }
  };

  const changeEmail = async () => {
    setBusyE(true); setErrE(""); setOkE(false);
    try {
      await api.post("/auth/change-email", email);
      setOkE(true); setEmail({ new_email: "", current_password: "" });
      onEmailChanged();
    } catch (e) {
      setErrE(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusyE(false); }
  };

  const isPassword = user.auth_provider === "email";
  if (!isPassword) {
    return <div className="font-mono text-sm text-[#8a8a8a]">Your account uses Google sign-in. Password &amp; email are managed by Google.</div>;
  }

  return (
    <div className="space-y-8">
      <section>
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#0a0a0a] mb-4">{c.change_pass}</div>
        <div className="space-y-3">
          <PasswordField tid="settings-current-pass" label={c.current_pass} value={pass.current} onChange={(v) => setPass({ ...pass, current: v })} autoComplete="current-password" />
          <PasswordField tid="settings-new-pass" label={c.new_pass} value={pass.next} onChange={(v) => setPass({ ...pass, next: v })} autoComplete="new-password" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <Feedback ok={okP} err={errP} c={c} />
          <BtnPrimary onClick={changePass} disabled={busyP || !pass.current || !pass.next} data-testid="settings-save-pass">
            {busyP ? <Loader2 size={12} className="animate-spin" /> : c.change_pass}
          </BtnPrimary>
        </div>
      </section>

      <section className="pt-6 border-t border-dashed border-[#e5e5e5]">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#0a0a0a] mb-1">{c.change_email}</div>
        <div className="font-mono text-[10px] text-[#8a8a8a] mb-4">{user.email}</div>
        <div className="space-y-3">
          <Row label={c.new_email}>
            <Input data-testid="settings-new-email" type="email" value={email.new_email} onChange={(e) => setEmail({ ...email, new_email: e.target.value })} />
          </Row>
          <PasswordField tid="settings-email-pass" label={c.password} value={email.current_password} onChange={(v) => setEmail({ ...email, current_password: v })} autoComplete="current-password" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <Feedback ok={okE} err={errE} c={c} />
          <BtnPrimary onClick={changeEmail} disabled={busyE || !email.new_email || !email.current_password} data-testid="settings-save-email">
            {busyE ? <Loader2 size={12} className="animate-spin" /> : c.change_email}
          </BtnPrimary>
        </div>
      </section>
    </div>
  );
}

function PrefsTab({ c, user, setUser, lang, setLang }) {
  const [preferred, setPreferred] = useState(user.preferred_lang || lang || "en");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr(""); setOk(false);
    try {
      const { data } = await api.patch("/auth/me", { preferred_lang: preferred });
      setUser({ ...user, ...data, password_hash: undefined });
      setLang(preferred);
      setOk(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <Row label={c.lang}>
        <div className="flex gap-2">
          {[
            { v: "en", label: "🇺🇸 English" },
            { v: "ru", label: "🇷🇺 Русский" },
            { v: "es", label: "🇪🇸 Español" },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`settings-lang-${o.v}`}
              onClick={() => setPreferred(o.v)}
              className={`px-4 py-2.5 border font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                preferred === o.v ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#0a0a0a] hover:bg-[#f5f5f5]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Row>
      <div className="flex items-center justify-between pt-2 border-t border-dashed border-[#e5e5e5]">
        <Feedback ok={ok} err={err} c={c} />
        <BtnPrimary onClick={save} disabled={busy} data-testid="settings-save-prefs">
          {busy ? <Loader2 size={12} className="animate-spin" /> : c.save}
        </BtnPrimary>
      </div>
    </div>
  );
}

function DangerTab({ c, user, logout }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user.role === "admin") {
    return <div className="font-mono text-sm text-[#8a8a8a]">Admin accounts cannot be self-deleted.</div>;
  }

  const remove = async () => {
    if (confirm !== "DELETE") return;
    setBusy(true); setErr("");
    try {
      await api.delete("/auth/me");
      logout();
      window.location.href = "/";
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="border-l-2 border-[#ff006e] pl-4 py-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#0a0a0a]">{c.delete_title}</div>
        <p className="mt-2 font-sans text-sm text-[#4a4a4a]">{c.delete_desc}</p>
      </div>
      <Row label={c.delete_confirm}>
        <Input data-testid="settings-delete-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" />
      </Row>
      {err && <div className="font-mono text-xs text-[#ff006e]">{err}</div>}
      <BtnDanger onClick={remove} disabled={busy || confirm !== "DELETE"} data-testid="settings-delete-btn">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
        {c.delete_btn}
      </BtnDanger>
    </div>
  );
}
